<template>
  <div :class="[$style.config, $style.wrap]">
    <div ref="target" :class="$style.content">
      <div :class="$style.copyWrap">
        <BaseHeading as="h2" size="h3" :class="$style.heading">
          <span v-html="data?.fields?.header" />
        </BaseHeading>

        <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />

        <div v-if="data?.fields?.ctas?.length" :class="$style.ctas">
          <CallToAction
            v-for="c in data?.fields?.ctas"
            :key="c?.sys?.id"
            :cta="c"
            class="js-ga-cta"
            :auto-ga-event="false"
          />
        </div>
      </div>

      <div :class="$style.products">
        <div ref="swiper" :class="['swiper', $style.swiper]">
          <div :class="'swiper-wrapper'">
            <div
              v-for="p in data?.fields?.products"
              :key="p?.sys?.id"
              :class="['swiper-slide', $style.card]"
              :style="p?.fields?.cssOverride as StyleValue"
            >
              <BaseCard>
                <template #media>
                  <BasePicture
                    v-if="p?.fields?.media?.fields?.file?.url"
                    :src="p?.fields?.media?.fields?.file?.url + '?w=600'"
                    :width="
                      p?.fields?.media?.fields?.file?.details?.image?.width
                    "
                    :height="
                      p?.fields?.media?.fields?.file?.details?.image?.height
                    "
                    :img-class="$style.cardImage"
                    alt=""
                  />
                </template>
                <template #body>
                  <div :class="$style.cardCopy">
                    <BaseHeading as="h3" size="h6" :class="$style.cardHeader">
                      {{ p?.fields?.title }}
                      <i :class="$style.cornerIcon">
                        <IconExternalLink width="16" height="16" />
                      </i>
                    </BaseHeading>
                    <template
                      v-if="
                        today &&
                        p?.fields?.startDatetime &&
                        p?.fields?.endDatetime
                      "
                    >
                      <span :class="$style.message">
                        {{ getSalesMessage(p) }}
                      </span>
                    </template>

                    <MarkedParser :md="p?.fields?.copy" />
                  </div>
                </template>
                <template #actions>
                  <BaseButton
                    :to="p?.fields?.link"
                    target="_blank"
                    presentation="invisible"
                    @click="sendEventToGa(p)"
                  >
                    {{ p?.fields?.title }}
                  </BaseButton>
                </template>
              </BaseCard>
            </div>
          </div>
        </div>

        <BaseButton
          :class="[
            $style.button,
            $style.left,
            hasReachedStart && $style.disabled,
          ]"
          presentation="strip"
          aria-label="previous slide"
          @click="onRequestPrev"
        >
          <IconChevronLeft />
        </BaseButton>
        <BaseButton
          :class="[
            $style.button,
            $style.right,
            hasReachedEnd && $style.disabled,
          ]"
          presentation="strip"
          aria-label="next slide"
          @click="onRequestNext"
        >
          <IconChevronRight />
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper'
import { formatDistanceToNow, isAfter } from 'date-fns'

import type { StyleValue } from 'vue'
import type {
  TypeProductCardWithoutUnresolvableLinksResponse,
  TypeProductsPromoWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import 'swiper/css'

const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeProductsPromoWithoutUnresolvableLinksResponse
}>()
// import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
// import observeIntersection from '~/mixins/observeIntersection.mixins'
// import { getDateLocale, formatLocalizedDate } from '~/utils/localizeDates'

// export default {
//   name: 'ProductsPromo',
//   components: {
//     Swiper,
//     SwiperSlide,
//     ExternalLink: () => import('~/assets/icons/external-link.svg?inline'),
//   },
//   mixins: [observeIntersection('target', { once: false }, false)],
//   props: {
//     data: {
//       type: Object,
//       required: false,
//       default: () => null,
//     },
//   },
//   data() {
//     return {
//       // swiper
//       swiperInstance: null,
const hasReachedStart = ref<boolean | undefined>(true)
const hasReachedEnd = ref<boolean | undefined>(false)

//       // datetime
const today = ref<Date>(new Date())
//     }
//   },
//   computed: {
//     computedHeader() {
//       return this.data?.header || ''
//     },
//     computedCopy() {
//       return this.data?.copy || ''
//     },
//     computedCtas() {
//       return this.data?.ctas || []
//     },
//     computedProducts() {
//       return this.data?.products || []
//     },
//     computedSwiperOptions() {
//       return {

//       }
//     },
//     computedCssOverride() {
//       return this.data?.cssOverride
//     },
//   },
//   watch: {
//     $isIntersecting(isIntersecting) {
//       if (isIntersecting) {
//         this.$emit('intersecting')
//         // this delay is required to ensure there's no
//         // ssr / client mismatch error
//         setTimeout(() => {
//           this.setToday()
//         }, 200)
//         this.$destroyObserver()
//       }
//     },
//   },
//   methods: {
//     setToday() {
//       today.value = new Date()
//     },
const getSalesMessage = (
  product: TypeProductCardWithoutUnresolvableLinksResponse,
) => {
  const startDatetime = product?.fields?.startDatetime || new Date()
  const endDatetime = product?.fields?.endDatetime || new Date()
  const type = product?.fields?.type

  const saleStarts = new Date(startDatetime)
  let localizationStringID

  // sale hasn't started yet
  if (isAfter(saleStarts, today.value)) {
    switch (type) {
      case 'Event':
        localizationStringID = 'eventStartsDate'
        break
      case 'Product':
      default:
        localizationStringID = 'onSaleDate'
    }

    const localizedCopy =
      localizedStrings.value[localizationStringID] || 'Starts on {{date}}'
    return localizedCopy?.replace(
      '{{date}}',
      formatLocalizedDate(startDatetime?.toString(), 'PP', locale.value),
    )
  }

  // sale has started, but has it ended?
  const saleEnds = new Date(endDatetime)

  // sale hasn't ended yet
  if (isAfter(saleEnds, today.value)) {
    switch (type) {
      case 'Event':
        localizationStringID = 'eventEndsInDays'
        break
      case 'Product':
      default:
        localizationStringID = 'saleEndsInDays'
    }

    const localizedCopy =
      localizedStrings.value[localizationStringID] || 'Ends in {{days}}'
    return localizedCopy?.replace(
      '{{days}}',
      formatDistanceToNow(saleEnds, {
        locale: getDateLocale(locale.value),
      }),
    )
  }

  // sale has ended
  switch (type) {
    case 'Event':
      localizationStringID = 'eventEnded'
      break
    case 'Product':
    default:
      localizationStringID = 'saleEnded'
  }

  return localizedStrings.value[localizationStringID] || 'Ended'
}

const onRequestPrev = () => swiperInstance.value?.slidePrev()
const onRequestNext = () => swiperInstance.value?.slideNext()

const sendEventToGa = (
  _product: TypeProductCardWithoutUnresolvableLinksResponse | undefined,
) => {
  // TBD: Client has to provide update analytics integrations. Leaving this for now
  //   if (
  //     typeof window.ga === 'function' &&
  //     _product?.eventCategory &&
  //     _product?.eventAction &&
  //     _product?.eventLabel
  //   ) {
  //     window.ga('send', 'event', {
  //       eventCategory: _product.eventCategory,
  //       eventAction: _product.eventAction,
  //       eventLabel: _product.eventLabel,
  //       transport: 'beacon',
  //     })
  //   }
}
// }
// }

// Swiper
const swiperElRef = useTemplateRef<HTMLElement>('swiper')
const swiperInstance = ref<null | Swiper>(null)
const initilizeSwiper = () => {
  if (swiperElRef.value) {
    swiperInstance.value = new Swiper(swiperElRef.value, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      breakpoints: {
        768: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 30,
        },
        1280: {
          slidesPerView: 4,
          spaceBetween: 30,
        },
      },
      ...props.data?.fields?.swiperOptions,
      on: {
        slideChange: () => {
          hasReachedStart.value = swiperInstance.value?.isBeginning
          hasReachedEnd.value = swiperInstance.value?.isEnd
        },
      },
    })
  }
}

onMounted(() => initilizeSwiper())
</script>

<style lang="scss" module>
.config {
  --content-grid-template-columns--base: 1fr;
  --content-grid-template-columns--sm: 1fr 1.5fr;
  --content-grid-template-columns--md: 1fr 2.5fr;
  --content-grid-template-columns--lg: 1fr 3fr;

  --content-grid-template-columns: var(--content-grid-template-columns--base);

  @include media('sm') {
    --content-grid-template-columns: var(--content-grid-template-columns--sm);
  }

  @include media('md') {
    --content-grid-template-columns: var(--content-grid-template-columns--md);
  }

  @include media('lg') {
    --content-grid-template-columns: var(--content-grid-template-columns--lg);
  }
}

.wrap {
  background: var(--wrap-background, #fff);
  padding: var(--wrap-padding, 0);
}

.leadingGraphic {
  display: block;
}

.content {
  display: grid;
  grid-template-columns: var(--content-grid-template-columns, 1fr);
  max-width: var(--content-max-width, 1440px);
  margin: 0 auto;
}

.copyWrap {
  color: inherit;
  padding: var(--copy-wrap-padding, 2rem);
  max-width: var(--copy-wrap-max-width, 40rem);
}

.ctas {
  margin: 1em 0;
  color: var(--ctas-color, inherit);
  display: flex;
}

.products {
  overflow: hidden;
  position: relative;
  padding: 2rem 1.25rem;
  text-align: center;

  .button.button {
    background: var(--arrow-button-background, white);
    border: 1px solid var(--arrow-button-border-color, black);
    border-radius: 50%;
    color: var(--arrow-button-color, black);

    display: inline-flex;
    align-items: center;
    width: var(--arrow-button-size, 48px);
    height: var(--arrow-button-size, 48px);
    margin: 1.5em 0.5em;
    z-index: 1;

    &.disabled {
      opacity: 0.5;

      @include media('sm') {
        opacity: 0;
      }
    }

    &:focus {
      color: var(--arrow-button-color--focus, black);
    }

    &:hover {
      background: var(--arrow-button-background--hover, black);
      color: var(--arrow-button-color--hover, white);
    }

    position: relative;

    @include media('sm') {
      position: absolute;
      top: 50%;
      margin-top: -1.5em;

      &.left {
        left: -8px;
      }
      &.right {
        right: -8px;
      }
    }
  }
}

.card {
  --card-actions-padding: 0;

  background: var(--card-background, white);
  max-width: var(--card-max-width, 18rem);
  padding: var(--card-padding, 0.5rem);
  box-shadow: var(--card-box-shadow, 0 2px 12px rgba(0, 0, 0, 0.25));
}

.card.card {
  height: auto;
}

.cardImage {
  max-width: 100%;
}

.message {
  position: absolute;
  left: 0;
  top: 2em;
  padding: 0 0.5rem;

  background: var(--message-background, var(--purple));
  color: var(--message-color, #fff);
  font-size: 12px;
  line-height: 2;
}

.cornerIcon {
  float: right;
  color: var(--corner-icon-color, inherit);
}

.cardCopy {
  color: var(--card-copy-color, inherit);
  text-align: var(--card-copy-text-align, center);
  line-height: 1.2;
}

.cardHeader.cardHeader {
  margin: var(--card-copy-margin, 0 0 1em);
  font-size: to-rem(20px);
}

.heading {
  color: var(--heading-color, inherit);
  margin: var(--heading-margin, 0 0 0.5rem);
}

.copy {
  color: var(--copy-color, inherit);
}
</style>
